import { Component, OnInit } from '@angular/core';
import { ActivatedRoute } from '@angular/router';
import { MatDialog } from '@angular/material/dialog';
import { CondominiumService, Condominium } from '../../services/condominium.service';
import { SubscriptionPlanDTO } from '../../services/subscription-plan.service';
import { GenericConfirmationDialogComponent } from '../../shared/delete-confirmation-dialog/generic-confirmation-dialog.component';
import { AlertDialogComponent } from '../../shared/alert-dialog/alert-dialog.component';

@Component({
  selector: 'app-condominium-subscription',
  standalone: false,
  templateUrl: './condominium-subscription.component.html',
  styleUrl: './condominium-subscription.component.sass'
})
export class CondominiumSubscriptionComponent implements OnInit {
  condominiumId?: number;
  condominium: Condominium | null = null;
  isLoading = true;
  selectedPlan: SubscriptionPlanDTO | null = null;
  isChangingPlan = false;

  constructor(
    private route: ActivatedRoute,
    private condominiumService: CondominiumService,
    private dialog: MatDialog
  ) {}

  ngOnInit(): void {
    const idParam = this.route.parent?.snapshot.paramMap.get('id');
    const id = idParam ? Number(idParam) : NaN;
    if (!isNaN(id)) {
      this.condominiumId = id;
      this.loadCondominium();
    } else {
      this.isLoading = false;
    }
  }

  loadCondominium(): void {
    if (!this.condominiumId) {
      return;
    }
    this.isLoading = true;
    this.condominiumService.getCondominiumDetails(this.condominiumId).subscribe({
      next: (c) => {
        this.condominium = c;
        this.isLoading = false;
      },
      error: () => {
        this.isLoading = false;
      }
    });
  }

  onPlanSelected(plan: SubscriptionPlanDTO | null): void {
    this.selectedPlan = plan;
  }

  onSubscriptionCancelled(): void {
    this.loadCondominium();
  }

  openChangePlanConfirmation(): void {
    if (!this.condominiumId || !this.selectedPlan) {
      return;
    }
    const currentName =
      this.condominium?.subscriptionType?.displayName ?? 'seu plano atual';
    const targetName = this.selectedPlan.displayName;
    let message = `Você está alterando seu plano de ${currentName} para ${targetName}.`;
    const deactivationIso = this.condominium?.subscriptionType?.deactivationDate;
    if (deactivationIso && this.isDeactivationDateInFuture(deactivationIso)) {
      const deactivationDate = this.formatLocaleDate(deactivationIso);
      message += ` Atenção: seu plano atual é válido até ${deactivationDate}. Ao confirmar, a mudança será imediata e o plano atual será encerrado.`;
    }
    const dialogRef = this.dialog.open(GenericConfirmationDialogComponent, {
      data: {
        message,
        confirmButtonText: 'Ok',
        cancelButtonText: 'Cancelar',
        cancelButtonStyle: 'raised',
        cancelButtonColor: 'accent',
        confirmButtonColor: 'primary'
      }
    });
    dialogRef.afterClosed().subscribe((confirmed: boolean) => {
      if (confirmed) {
        this.submitPlanChange();
      }
    });
  }

  private submitPlanChange(): void {
    if (!this.condominiumId || !this.selectedPlan) {
      return;
    }
    const targetPlan = this.selectedPlan;
    this.isChangingPlan = true;
    this.condominiumService
      .changeSubscription(this.condominiumId, {
        subscriptionPlanId: targetPlan.id
      })
      .subscribe({
        next: () => {
          this.isChangingPlan = false;
          this.dialog
            .open(AlertDialogComponent, {
              data: {
                message: `Alteração concluída. Seu novo plano ${targetPlan.displayName} já está ativo`,
                okText: 'Ok'
              }
            })
            .afterClosed()
            .subscribe(() => {
              this.loadCondominium();
            });
        },
        error: () => {
          this.isChangingPlan = false;
          this.dialog
            .open(AlertDialogComponent, {
              data: {
                message: 'Algo deu errado. Tente novamente mais tarde',
                okText: 'Ok'
              }
            })
            .afterClosed()
            .subscribe(() => {
              this.loadCondominium();
            });
        }
      });
  }

  private isDeactivationDateInFuture(iso: string): boolean {
    const part = iso.split('T')[0];
    const [y, m, d] = part.split('-').map(Number);
    const deact = new Date(y, m - 1, d);
    const today = new Date();
    const todayStart = new Date(today.getFullYear(), today.getMonth(), today.getDate());
    return deact > todayStart;
  }

  private formatLocaleDate(iso: string): string {
    const part = iso.split('T')[0];
    const [y, m, d] = part.split('-').map(Number);
    const date = new Date(y, m - 1, d);
    return date.toLocaleDateString('pt-BR', { day: 'numeric', month: 'long', year: 'numeric' });
  }
}
